import React, { useContext, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import moment from 'moment';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
// Context
import useCallPerformanceContext from '../context/useCallPerformanceContext';
// Api
import { getManagerFilter, getRepFilter } from '../../../api/filterOptionsApi';
// Helper Functions
import {
  filterValue,
  filterManagerOptions,
  filterRepOptions,
} from '../../../helpers/filterFunctions';

import {
  postCallConcentrationBreakdown,
  postCallConcentrationBreakdownExport,
} from '../../../api/crmDashboard';

export default function CallConcentrationModal() {
  const { filterForm, callConcentrationModal, setCallConcentrationModal } =
    useContext(useCallPerformanceContext);

  const modalFilterFormDefault = {
    team: filterForm?.team,
    manager_id: { label: 'ALL', value: null },
    rep_id: filterForm?.rep_id,
    date_from: filterForm?.date_from,
    date_to: filterForm?.date_to,
  };

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState({ concentration_percentage: 0, target: 0, concentration: 0 });
  const [modalFilterForm, setModalFilterForm] = useState(modalFilterFormDefault);
  const [managerOptions, setManagerOptions] = useState([]);
  const [repOptions, setRepOptions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [sortModel, setSortModel] = useState([
    {
      field: 'id',
      sort: 'desc',
    },
  ]);

  const columns = [
    {
      field: 'rsm_name',
      headerName: 'RSM Name',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'manager_name',
      headerName: 'Manager Name',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'mpr_name',
      headerName: 'MPR Name',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'md',
      headerName: 'MD',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        row.rsm_name !== 'TOTAL'
          ? row.md
          : Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.md),
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.frequency),
    },
    {
      field: 'plotted',
      headerName: 'Plotted',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.plotted),
    },
    {
      field: 'actual',
      headerName: 'Actual',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.actual),
    },
    {
      field: 'concentration_percentage',
      headerName: 'Concentration',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => `${parseFloat(row.concentration_percentage).toFixed(2)}%`,
    },
  ];

  const handleCancel = () => {
    setCallConcentrationModal(false);
  };

  const loadManagersDropdown = async () => {
    const request = {
      team: filterValue(filterForm?.team),
      rep_id: filterValue(modalFilterForm?.rep_id),
      date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
      date_to: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
    };
    const response = await getManagerFilter(request);
    const { code, data } = response.data;

    if (code === '200') {
      const dropdownValue = filterManagerOptions(data);

      setManagerOptions([{ label: 'ALL', value: null }, ...dropdownValue]);
    }
  };

  const loadRepDropdown = async () => {
    const request = {
      team: filterValue(filterForm?.team),
      manager_id: filterValue(modalFilterForm?.manager_id),
      date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
      date_to: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
    };
    const reponse = await getRepFilter(request);
    const { code, data } = reponse.data;
    if (code === '200') {
      const dropdownValue = filterRepOptions(data);
      setRepOptions([{ label: 'ALL', value: null }, ...dropdownValue]);
    }
  };

  const handleGenerateFilter = async (clear = null) => {
    try {
      setLoading(true);
      let request;
      if (clear === 'reset') {
        request = {
          team: filterForm?.team?.value,
          manager_id: modalFilterFormDefault?.manager_id?.value,
          rep_id: modalFilterFormDefault?.rep_id?.value,
          with_reached: modalFilterFormDefault?.with_reached,
          date_from: moment(modalFilterFormDefault?.date_from).format('YYYY-MM-DD'),
          date_to: moment(modalFilterFormDefault?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          team: filterForm?.team?.value,
          manager_id: modalFilterForm?.manager_id?.value,
          rep_id: modalFilterForm?.rep_id?.value,
          with_reached: modalFilterForm?.with_reached,
          date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(modalFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallConcentrationBreakdown(request);
      const { code, target, concentration, data } = response.data;

      if (code === '200') {
        setTotal({
          ...total,
          concentration_percentage: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
            response?.data?.concentration_percentage,
          ),
          target: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(target),
          concentration: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
            concentration,
          ),
        });
        setTableData(data);
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeFilter = (e, row, name) => {
    setModalFilterForm({
      ...modalFilterForm,
      [name]: row,
    });
  };

  const handleClearFilter = () => {
    setModalFilterForm(modalFilterFormDefault);
    handleGenerateFilter('reset');
  };

  const handleExportBreakdown = async () => {
    try {
      toast.loading('Please wait ...');

      const request = {
        team: filterForm?.team?.value,
        manager_id: modalFilterForm?.manager_id?.value,
        rep_id: modalFilterForm?.rep_id?.value,
        date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
        date_to: moment(modalFilterForm?.date_to).format('YYYY-MM-DD'),
      };

      const res = await postCallConcentrationBreakdownExport(request);

      const { code, result } = res.data.response;
      if (code === '200') {
        window.location.href = result;
        toast.success(`Successfully downloaded file: ${res?.data?.response?.file_name}`);
      }
      toast.dismiss();
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const pinnedRows = {
    top: tableData?.length ? [tableData[0]] : [],
  };

  useEffect(() => {
    loadManagersDropdown();
    loadRepDropdown();
  }, [modalFilterForm]);

  useEffect(() => {
    handleGenerateFilter();
  }, []);

  return (
    <div>
      <Dialog open={callConcentrationModal} onClose={handleCancel} maxWidth='auto' fullWidth>
        <DialogTitle>
          Call Concentration Breakdown
          <IconButton
            aria-label='close'
            onClick={handleCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pl={2} pt={2}>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Autocomplete
                disablePortal
                id='manager_id'
                disableClearable
                fullWidth
                options={managerOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='Manager' />}
                size='small'
                value={modalFilterForm?.manager_id}
                onChange={(e, newValue) => handleChangeFilter(e, newValue, 'manager_id')}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Autocomplete
                disablePortal
                id='rep_id'
                disableClearable
                fullWidth
                options={repOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='MPR' />}
                size='small'
                value={modalFilterForm?.rep_id}
                onChange={(e, newValue) => handleChangeFilter(e, newValue, 'rep_id')}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Button variant='contained' color='success' onClick={handleGenerateFilter}>
                Generate
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => handleClearFilter()}
                sx={{ marginLeft: '10px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={6} lg={6} pl={3} pt={3}>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>
                Concentration %:{' '}
                <span style={{ color: '#a50000' }}>{total.concentration_percentage}%</span>
                <span style={{ marginLeft: '50px' }}>Target: {total.target}</span>
                <span style={{ marginLeft: '50px' }}>
                  Concentration: <span style={{ color: '#a50000' }}>{total.concentration}</span>
                </span>
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }} />
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6} pl={3} pt={3}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant='contained'
                  color='warning'
                  onClick={() => handleExportBreakdown()}
                  startIcon={<CloudDownloadIcon />}
                  sx={{
                    marginLeft: '10px',
                    paddingTop: '10px',
                  }}
                >
                  Export
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} pl={2} pt={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Box
                className='table-container'
                sx={{
                  height: `600px`,
                  '& .super-app-theme--header': {
                    fontWeight: 'bold',
                    backgroundColor: '#808080',
                    color: '#fff',
                  },
                  '& .MuiDataGrid-pinnedRows': { fontWeight: 'bold', backgroundColor: '#FBCEB1' },
                }}
              >
                <DataGridPro
                  loading={loading}
                  rows={tableData}
                  columns={columns}
                  sortModel={sortModel}
                  experimentalFeatures={{ rowPinning: true }}
                  pinnedRows={pinnedRows}
                  initialState={{
                    ...tableData.initialState,
                    pagination: {
                      pageSize: 50,
                    },
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  pagination
                  onSortModelChange={(model) => setSortModel(model)}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='error' onClick={handleCancel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
